<template>
  <div class="p-4 md:p-6 lg:p-8 bg-app-bg-light dark:bg-app-bg-dark min-h-screen">
    <!-- Header -->
    <div class="flex items-center gap-3 mb-6">
      <button
        @click="$router.push('/inicio')"
        class="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-custom-primary dark:hover:text-custom-primary transition-colors border-0 bg-transparent"
      >
        <i class="bx bx-arrow-back text-lg"></i>
        Regresar
      </button>
      <div class="h-5 w-px bg-slate-300 dark:bg-slate-600"></div>
      <div>
        <h1 class="text-xl md:text-2xl font-bold text-slate-800 dark:text-slate-100">Facturas Anuladas</h1>
        <p class="text-xs text-slate-500 dark:text-slate-400">Listado de facturas FEL anuladas localmente (no implica anulación ante SAT)</p>
      </div>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-slate-800 rounded-xl p-4 md:p-6 shadow-sm mb-6">
      <div class="flex flex-wrap gap-3 items-end">
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Desde</label>
          <input type="date" v-model="filtro.desde" class="form-control form-control-sm" />
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Hasta</label>
          <input type="date" v-model="filtro.hasta" class="form-control form-control-sm" />
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">UUID</label>
          <input type="text" v-model="filtro.uuid" placeholder="UUID de la factura" class="form-control form-control-sm" />
        </div>
        <div>
          <label class="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">NIT Receptor</label>
          <input type="text" v-model="filtro.receptor_nit" placeholder="NIT" class="form-control form-control-sm" />
        </div>
        <button @click="cargar(1)" class="btn btn-outline-secondary btn-sm">
          <i class="bx bx-search mr-1"></i> Filtrar
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-10">
      <i class="bx bx-loader-alt bx-spin text-3xl text-custom-primary"></i>
    </div>

    <div v-else>
      <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-xs md:text-sm">
            <thead>
              <tr class="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
                <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Serie-Número</th>
                <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">UUID</th>
                <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Fecha emisión</th>
                <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Fecha anulación</th>
                <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Receptor</th>
                <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">NIT</th>
                <th class="px-3 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">Gran total</th>
                <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Motivo</th>
                <th class="px-3 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Anulado por</th>
                <th class="px-3 py-3 text-center font-semibold text-slate-700 dark:text-slate-300">Ver</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!facturas.length">
                <td colspan="10" class="text-center py-6 text-slate-400">No hay facturas anuladas en el período seleccionado.</td>
              </tr>
              <tr v-for="f in facturas" :key="f.id" class="border-b border-slate-100 dark:border-slate-700">
                <td class="px-3 py-2 whitespace-nowrap">{{ f.serie }}-{{ f.numero }}</td>
                <td class="px-3 py-2" style="max-width: 220px; word-break: break-all;">{{ f.uuid }}</td>
                <td class="px-3 py-2 whitespace-nowrap">{{ formatFecha(f.fecha_cert_dt) }}</td>
                <td class="px-3 py-2 whitespace-nowrap">{{ formatFecha(f.deleted_at) }}</td>
                <td class="px-3 py-2">{{ f.receptor_nombre || '—' }}</td>
                <td class="px-3 py-2">{{ f.receptor_nit || '—' }}</td>
                <td class="px-3 py-2 text-right font-mono">Q{{ fmt(f.gran_total) }}</td>
                <td class="px-3 py-2">{{ f.motivo_anulacion || '—' }}</td>
                <td class="px-3 py-2">{{ nombreEmpleado(f.anulado_por_relacion) }}</td>
                <td class="px-3 py-2 text-center">
                  <button @click="verFactura(f)" class="btn btn-outline-primary btn-sm">
                    <i class="bi bi-eye me-1"></i> Ver
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginación -->
        <div class="flex items-center justify-between px-3 py-3 border-t border-slate-100 dark:border-slate-700" v-if="paginacion.last_page > 1">
          <span class="text-xs text-slate-500 dark:text-slate-400">
            Página {{ paginacion.current_page }} de {{ paginacion.last_page }} ({{ paginacion.total }} registro(s))
          </span>
          <div class="btn-group">
            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="paginacion.current_page <= 1"
              @click="cargar(paginacion.current_page - 1)"
            >
              <i class="bx bx-chevron-left"></i> Anterior
            </button>
            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="paginacion.current_page >= paginacion.last_page"
              @click="cargar(paginacion.current_page + 1)"
            >
              Siguiente <i class="bx bx-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <FelPreviewModal
      v-if="facturaSeleccionada"
      :uuid="facturaSeleccionada.uuid"
      formato="carta"
      @cerrar="facturaSeleccionada = null"
    />
  </div>
</template>

<script>
import axios from 'axios';
import FelPreviewModal from '@/components/shared/FelPreviewModal.vue';

export default {
  name: 'FacturasAnuladas',
  components: { FelPreviewModal },
  data() {
    return {
      loading: false,
      filtro: { desde: '', hasta: '', uuid: '', receptor_nit: '' },
      facturas: [],
      paginacion: { current_page: 1, last_page: 1, total: 0 },
      facturaSeleccionada: null,
    };
  },
  methods: {
    fmt(num) {
      return new Intl.NumberFormat('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(num ?? 0);
    },
    formatFecha(f) {
      if (!f) return '—';
      return new Date(f.replace(' ', 'T')).toLocaleString('es-GT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    nombreEmpleado(empleado) {
      if (!empleado) return '—';
      return `${empleado.Nombres ?? ''} ${empleado.Apellidos ?? ''}`.trim() || '—';
    },
    verFactura(f) {
      this.facturaSeleccionada = f;
    },
    async cargar(page = 1) {
      this.loading = true;
      try {
        const { data } = await axios.get('facturas-fel/anuladas', {
          params: { ...this.filtro, page },
        });
        this.facturas = (data.data || []).map((f) => ({
          ...f,
          fecha_cert_dt: f.fecha_cert ? new Date(f.fecha_cert).toISOString() : null,
          anulado_por_relacion: f.anuladoPor,
        }));
        this.paginacion = {
          current_page: data.current_page,
          last_page: data.last_page,
          total: data.total,
        };
      } catch (e) {
        console.error(e);
        alert(e.response?.data?.message || 'Error al cargar el listado de facturas anuladas.');
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.cargar();
  },
};
</script>
